/**
 * Seat data shape + summary maths shared across profile / lobby surfaces.
 *
 * The original `CoinSeatsCard` React component (and its V1-only
 * `/coin/{addr}` + `/coins/{poolId}` deep-link builder) lived here, but
 * it stopped rendering anywhere when the legacy V1 detail-page tree was
 * deleted. We retain only:
 *   - The `CoinSeatsData` / `SeatPosition` / `CoinSeatsSummary` types,
 *     consumed by `TileManagementModal`, `ProfileTilesList`,
 *     `useLobbySeatHoldings`, `build-hud-snapshot`, and `seat-utils`.
 *   - The pure `calculateSummary` helper, consumed by `profile-client`
 *     and `profile-tiles-list` to roll up portfolio totals.
 *
 * Filename is preserved so existing barrel exports (`@/components/seats`)
 * continue to resolve without churn across the codebase.
 */

import type { RewardKind } from '@/lib/rewards';

export interface SeatPosition {
  seatIndex: number;
  selfAssessedPrice: bigint;
  balance: bigint;
  weeklyEarnings: bigint;
  weeklyTax: bigint;
  runwaySeconds: number;
  heldSince: number;
  isInCooldown?: boolean;
  cooldownMinutesRemaining?: number;
  liveWeeklyNet?: bigint;
  earningsKind?: RewardKind;
  earningsSymbol?: string;
  earningsDecimals?: number;
  /**
   * Tax-depleted "zombie" seat — the holder is still recorded onchain
   * (`isActive=true` in the indexer) but the deposit has fallen to zero
   * so a `pokeTax` from anyone would forfeit it. Surfaces only when the
   * caller passed `{ includeForfeited: true }` to `transformToSeatPosition`
   * — by default these seats are filtered to `null`, matching the
   * portfolio/profile pages that don't want forfeit-pending clutter.
   */
  isForfeited?: boolean;
}

export interface CoinSeatsData {
  poolId: string;
  poolName: string;
  poolSymbol: string;
  poolImageUrl?: string;
  memecoinAddress?: string;
  managerAddress?: string;
  floorPrice: bigint;
  activeSeatCount?: number;
  isGraduated?: boolean;
  isV2Grid?: boolean;
  earningsKind?: RewardKind;
  earningsSymbol?: string;
  earningsDecimals?: number;
  taxToken?: string;
  taxTokenDecimals?: number;
  totalSeats?: number;
  seats: SeatPosition[];
}

export interface CoinSeatsSummary {
  totalSeats: number;
  totalCoins: number;
  totalBalance: bigint;
  totalSeatValue: bigint;
  totalWeeklyNet: bigint;
  totalWeeklyEarnings: bigint;
  totalWeeklyTax: bigint;
  shortestRunway: number;
  lowestPrice: bigint;
}

export function calculateSummary(
  coins: CoinSeatsData[],
  takeoverPriceUsd: number = 0,
): CoinSeatsSummary {
  let totalSeats = 0;
  let totalBalance = 0n;
  let totalSeatValue = 0n;
  let totalWeeklyEarnings = 0n;
  let totalWeeklyTax = 0n;
  let shortestRunwaySeconds = Infinity;
  let lowestPrice = BigInt(Number.MAX_SAFE_INTEGER);

  for (const coin of coins) {
    totalSeats += coin.seats.length;
    const isNative = coin.earningsKind === 'native';
    const isNonUsdc =
      coin.taxToken && coin.taxToken !== '' && !isUsdcToken(coin.taxToken);
    const conv = !!(isNonUsdc && takeoverPriceUsd > 0);
    const dec = coin.taxTokenDecimals ?? 18;

    for (const seat of coin.seats) {
      const seatPrice = conv
        ? toUsdc6(seat.selfAssessedPrice, dec, takeoverPriceUsd)
        : seat.selfAssessedPrice;
      const seatBalance = conv
        ? toUsdc6(seat.balance, dec, takeoverPriceUsd)
        : seat.balance;
      const seatEarn = conv
        ? toUsdc6(seat.weeklyEarnings, dec, takeoverPriceUsd)
        : seat.weeklyEarnings;
      const seatTax = conv
        ? toUsdc6(seat.weeklyTax, dec, takeoverPriceUsd)
        : seat.weeklyTax;

      totalBalance += seatBalance;
      totalSeatValue += seatPrice;
      if (!isNative) {
        totalWeeklyEarnings += seatEarn;
      }
      totalWeeklyTax += seatTax;

      if (seatPrice < lowestPrice) {
        lowestPrice = seatPrice;
      }

      if (seat.runwaySeconds < shortestRunwaySeconds) {
        shortestRunwaySeconds = seat.runwaySeconds;
      }
    }
  }

  return {
    totalSeats,
    totalCoins: coins.length,
    totalBalance,
    totalSeatValue,
    totalWeeklyNet: totalWeeklyEarnings - totalWeeklyTax,
    totalWeeklyEarnings,
    totalWeeklyTax,
    shortestRunway:
      shortestRunwaySeconds === Infinity ? 0 : shortestRunwaySeconds / 604800,
    lowestPrice:
      lowestPrice === BigInt(Number.MAX_SAFE_INTEGER) ? 0n : lowestPrice,
  };
}

const USDC_ADDRESSES = new Set([
  '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
  '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913',
  '0xaf88d065e77c8cc2239327c5edb3a432268e5831',
]);

function isUsdcToken(address: string): boolean {
  return USDC_ADDRESSES.has(address.toLowerCase());
}

function toUsdc6(amount: bigint, decimals: number, priceUsd: number): bigint {
  return BigInt(Math.floor((Number(amount) / 10 ** decimals) * priceUsd * 1e6));
}
